import { Button } from '@finn-auto/design-system/atoms/button';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { cn } from '../../../../shared';
import type { BookingSlot } from '../../services/interviewBookingApi';
import { fmtLongDay, fmtTime, type BookingDay } from './format';
import { InlineError } from './BookingPanels';

/**
 * Day strip + times for the selected day (the Calendly shape).
 *
 * Slots are already merged panel availability, so every button here is bookable
 * as far as the last fetch knew — the proxy re-verifies on confirm.
 */
export function SlotPicker({
  days,
  activeDay,
  onSelectDay,
  onSelectSlot,
  onRefresh,
  refreshing,
  heading = 'Select a day',
  onBack,
  error = null,
  selectedStart = null,
}: {
  days: BookingDay[];
  activeDay: BookingDay | null;
  onSelectDay: (key: string) => void;
  onSelectSlot: (slot: BookingSlot) => void;
  onRefresh: () => void;
  refreshing: boolean;
  /** Reused for rescheduling, where the wording differs. */
  heading?: string;
  /** Only shown when there is somewhere to go back to (i.e. rescheduling). */
  onBack?: () => void;
  error?: string | null;
  /**
   * Start of the already-chosen slot, marked as selected.
   *
   * The candidate flow advances to another step the moment a time is picked, so it never needs
   * this. The FINN-side Move dialog keeps the picker on screen while the recruiter fills in a
   * reason, and without a marked chip there is nothing to show WHICH time is about to be sent.
   */
  selectedStart?: string | null;
}) {
  if (days.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
        <p className="text-sm font-medium text-gray-700">No times available</p>
        <p className="mt-1 text-xs text-gray-500">
          The panel has no shared free slot in the booking window. Please contact your recruiter.
        </p>
        <Button size="sm" variant="outline" className="mt-3" onClick={onRefresh}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div>
      {onBack ? (
        <Button size="sm" variant="ghost" type="button" onClick={onBack} className="mb-3">
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Keep my current time
        </Button>
      ) : null}

      {error ? <InlineError message={error} /> : null}

      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{heading}</h3>
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', refreshing && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2">
        {days.map((day) => {
          const active = day.key === activeDay?.key;
          return (
            <button
              key={day.key}
              type="button"
              onClick={() => onSelectDay(day.key)}
              aria-pressed={active}
              className={cn(
                'flex min-w-[64px] shrink-0 flex-col items-center rounded-lg border px-3 py-2 transition-colors',
                active
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'
              )}
            >
              <span className="text-[11px] uppercase tracking-wide opacity-70">{day.weekday}</span>
              <span className="text-lg font-semibold leading-tight">{day.dayNum}</span>
              <span className="text-[11px] opacity-70">{day.month}</span>
              <span className="mt-0.5 text-[10px] opacity-70">{day.slots.length} slots</span>
            </button>
          );
        })}
      </div>

      {activeDay ? (
        <>
          <h3 className="mb-2 mt-4 text-sm font-semibold text-gray-900">
            {activeDay.slots[0] ? fmtLongDay(activeDay.slots[0].start) : ''}
          </h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {activeDay.slots.map((slot) => {
              const selected = selectedStart === slot.start;
              return (
                <button
                  key={slot.start}
                  type="button"
                  onClick={() => onSelectSlot(slot)}
                  aria-pressed={selected}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                    selected
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-300 text-gray-800 hover:border-gray-900 hover:bg-gray-900 hover:text-white'
                  )}
                >
                  {fmtTime(slot.start)}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
